import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MaskedLines } from "@/components/Reveal";
import { WhatsAppButton, CallButton } from "@/components/CTAButtons";
import { IMAGES, CONTACT } from "@/data/site";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] w-full overflow-hidden grain" data-testid="hero">
      {/* Parallax image */}
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Frota de caminhões-guincho Auto Socorro Pimenta"
          className="h-full w-full object-cover"
          fetchPriority="high"
        />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-black" />
      <div className="absolute inset-0 bg-gradient-to-t from-brand-obsidian via-transparent to-brand-obsidian/60" />

      {/* Content */}
      <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-5 pb-24 pt-32 md:px-8 md:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="mb-6 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-brand-red" />
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-white/70">
            Rondônia & Mato Grosso · Há {CONTACT.years}+ anos
          </span>
        </motion.div>

        <h1 className="font-display text-[12vw] uppercase leading-[0.88] tracking-tight text-white sm:text-6xl md:text-7xl lg:text-8xl">
          <MaskedLines
            lines={["Auto Socorro", "e Guincho", <span key="24" className="text-brand-red">24 Horas</span>]}
            delay={0.2}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.7 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-white/75 md:text-lg"
        >
          Há mais de {CONTACT.years} anos oferecendo atendimento rápido, seguro e especializado em
          guincho leve, guincho pesado, transporte de máquinas e assistência 24 horas em Rondônia e Mato Grosso.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-9 flex flex-col gap-4 sm:flex-row"
        >
          <WhatsAppButton testid="hero-whatsapp" />
          <CallButton testid="hero-call" />
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 md:block">
        <div className="flex h-11 w-6 items-start justify-center rounded-full border border-white/30 p-1.5">
          <motion.span
            className="h-2 w-1 rounded-full bg-brand-red"
            animate={{ y: [0, 12, 0] }}
            transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
          />
        </div>
      </div>
    </section>
  );
}
